import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import DashboardShell from "../components/common/DashboardShell";
import { LeagueApi } from "../utils/leagueApi";

const REASON_CODES = [
  { value: "scheduling", label: "Scheduling" },
  { value: "dispute", label: "Dispute resolution" },
  { value: "error_correction", label: "Error correction" },
  { value: "policy", label: "Policy decision" },
  { value: "other", label: "Other" },
];

// Shared reason-code + narrative fields — every override needs both.
function Justification({ reasonCode, narrative, onReason, onNarrative }) {
  return (
    <>
      <select
        aria-label="Reason code"
        className="w-full rounded border bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
        value={reasonCode}
        onChange={(e) => onReason(e.target.value)}
      >
        <option value="">Reason code…</option>
        {REASON_CODES.map((r) => (
          <option key={r.value} value={r.value}>
            {r.label}
          </option>
        ))}
      </select>
      <textarea
        aria-label="Narrative"
        className="w-full rounded border bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
        placeholder="Narrative (required)"
        rows={2}
        value={narrative}
        onChange={(e) => onNarrative(e.target.value)}
      />
    </>
  );
}

Justification.propTypes = {
  reasonCode: PropTypes.string.isRequired,
  narrative: PropTypes.string.isRequired,
  onReason: PropTypes.func.isRequired,
  onNarrative: PropTypes.func.isRequired,
};

function OverrideForm({ title, fields, kind, onDone }) {
  const [values, setValues] = useState({});
  const [reasonCode, setReasonCode] = useState("");
  const [narrative, setNarrative] = useState("");
  const [msg, setMsg] = useState("");

  const submit = async () => {
    setMsg("");
    try {
      await LeagueApi.commissionerOverride(kind, {
        ...values,
        reason_code: reasonCode,
        narrative,
      });
      setMsg("Applied and logged.");
      setValues({});
      setReasonCode("");
      setNarrative("");
      onDone();
    } catch {
      setMsg("Rejected — a reason code and narrative are required.");
    }
  };

  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
      <h3 className="mb-3 font-semibold text-gray-700 dark:text-gray-300">
        {title}
      </h3>
      <div className="space-y-3">
        {fields.map((f) => (
          <input
            key={f.name}
            aria-label={f.label}
            className="w-full rounded border bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
            placeholder={f.label}
            type={f.type || "text"}
            value={values[f.name] || ""}
            onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
          />
        ))}
        <Justification
          narrative={narrative}
          reasonCode={reasonCode}
          onNarrative={setNarrative}
          onReason={setReasonCode}
        />
        <button
          className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          type="button"
          onClick={submit}
        >
          Apply override
        </button>
        {msg ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">{msg}</p>
        ) : null}
      </div>
    </section>
  );
}

OverrideForm.propTypes = {
  title: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  kind: PropTypes.string.isRequired,
  onDone: PropTypes.func.isRequired,
};

export default function CommissionerDashboard() {
  const [log, setLog] = useState([]);

  const loadLog = () => {
    LeagueApi.commissionerLog()
      .then(setLog)
      .catch(() => undefined);
  };

  useEffect(loadLog, []);

  return (
    <DashboardShell>
      <div className="mx-auto max-w-3xl space-y-6 p-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Commissioner Dashboard
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Every override is permanently recorded in the audit log and requires a
          reason code and narrative.
        </p>

        <OverrideForm
          fields={[
            { name: "team", label: "Team ID" },
            { name: "points", label: "Points (+/-)", type: "number" },
            { name: "level", label: "Level (local/state/regional/national)" },
          ]}
          kind="ledger"
          title="Frozen-ledger correction"
          onDone={loadLog}
        />
        <OverrideForm
          fields={[
            { name: "phase", label: "Phase ID" },
            { name: "ends_at", label: "New end (ISO datetime)" },
          ]}
          kind="timeline"
          title="Timeline override"
          onDone={loadLog}
        />
        <OverrideForm
          fields={[
            { name: "team", label: "Team ID" },
            { name: "user", label: "User ID" },
            { name: "action", label: "Action (add/remove)" },
          ]}
          kind="roster"
          title="Roster dispute override"
          onDone={loadLog}
        />

        <section>
          <h2 className="mb-3 font-semibold text-gray-700 dark:text-gray-300">
            Audit log
          </h2>
          {log.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">
              No overrides recorded yet.
            </p>
          ) : (
            <ul className="space-y-2">
              {log.map((entry) => (
                <li
                  key={entry.id}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-800 dark:text-gray-100">
                      {entry.action_display}
                    </span>
                    <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-400">
                      {entry.reason_code}
                    </span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400">
                    {entry.target}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {entry.narrative}
                    {entry.actor ? ` — ${entry.actor}` : ""}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </DashboardShell>
  );
}
